<template>
  <div class="wrapper">
    <div class="title-content">
      {{ $t('serviceOutlets.service_point_query') }}
    </div>
    <client-only>
      <el-input
        :class="[{ listPull: showList }, 'search']"
        v-model="searchValue"
        @search="onSearch"
        @focus="focusSearch"
        @blur="hideSearch"
        @mousedown="handleMouseDown"
        @input="searchChange"
        autocomplete="off"
        :placeholder="$t('serviceOutlets.search_location')">
        <template #suffix>
          <svg-icon name="icon-search-new" class="ic_search" @click="onClickButton" />
        </template>
      </el-input>
    </client-only>

    <div v-if="showList" :class="[{ contentPull: showList }, 'search-content']">
      <div class="content">
        <div class="item" v-for="(item, ix) in searchList" :key="ix" @click="searItemClick(item)">
          <div v-html="lightText(item.name)" class="title"></div>
          <div class="desc">{{ item.address }}</div>
        </div>
        <div v-if="!searchList.length" class="no-data-content">
          <img :src="imgNoData" />
          <p class="tips">
            {{ $t('serviceOutlets.search_no_data_tip') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { searchNearList } from '@support/api';
import { getAsyncData } from '@support/utils';
import lodash from 'lodash';
import imgNoData from '@/assets/images/img_no_data.png';
const { debounce } = lodash;
const { $sensorTrack } = useNuxtApp();

const props = defineProps({
  longAndLat: { type: Object, default: {} }
});
const searchValue = ref('');

const searchList: any = ref([]);
const showList: any = ref(false);
const emits = defineEmits(['clickSearchItem']);

const lightText = (text: String) => {
  const regex = new RegExp(searchValue.value, 'gi');
  // 替换匹配到的词汇
  const innerHTML: String = text.replace(regex, match => {
    return `<span class="highlightColor">${match}</span>`;
  });
  return innerHTML;
};

const hideSearch = () => {
  let timer: any = setTimeout(() => {
    $sensorTrack('overseas_service_network_search_cancelled_click', {});
    showList.value = false;
    clearTimeout(timer);
    timer = null;
  }, 200);
};

const focusSearch = (val: any) => {
  if (searchValue.value) searchChange(val.target.value);
};
const onSearch = (val: any) => {
  showList.value = true;
};
const onClickButton = () => {
  showList.value = false;
};
const searchChange = (value: any) => {
  getList();
};
const handleMouseDown = (event: any) => {
  // 判断是否是触摸板按下
  if (event.pointerType === 'touch') {
    searchValue.value = event.target.value;
    emits('clickSearchItem', event.target.value);
  }
};
const getList = debounce(() => {
  getSearchList();
}, 1000);

async function getSearchList() {
  const { data } = await useAsyncData(() =>
    searchNearList({
      keyword: searchValue.value,
      longitude: props.longAndLat.longitude,
      latitude: props.longAndLat.latitude
    })
  );
  const value = getAsyncData(data).data;
  searchList.value = value; // valueData;
  showList.value = true;
}
const searItemClick = (item: any) => {
  searchValue.value = item.name;
  emits('clickSearchItem', item);
  $sensorTrack('overseas_service_network_search_box_click', {});
};
</script>

<style scoped lang="scss">
p {
  margin: 0;
}
.overlay {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}
.wrapper {
  height: min-content;
  position: relative;
  box-sizing: border-box;
  width: calc(100% - 32px);
  border-radius: 8px;
  background: #fff;
  left: 16px;
  top: 16px;
  margin-bottom: 26px;
  .listPull {
    :deep(.el-input__wrapper) {
      // border-top-left-radius: ;
      border-top-left-radius: 30px;
      border-top-right-radius: 30px;
      border-bottom-left-radius: unset !important;
      border-bottom-right-radius: unset !important;
    }
  }

  .title-content {
    font-weight: 600;
    font-size: 32px;
    float: left;
    margin-top: 16px;
    margin-left: 20px;
    margin-bottom: 10px;
    width: 100%;
    height: 40px;
  }
  .search {
    width: calc(100% - 40px);
    height: 48px;
    background: rgba(255, 255, 255, 0.76);
    border-radius: 10px 10px 10px 10px;
    border: 1px solid #9e9e9e;
    opacity: 0.6;
    margin-top: 4px;
    margin-left: 20px;
    margin-bottom: 16px;

    :deep(.el-input__inner) {
      font-size: 16px;
      color: #000;
      font-weight: 700;
    }

    :deep(.el-input__inner::placeholder) {
      color: rgba(0, 0, 0, 0.5);
      font-weight: 400;
    }
    :deep(.el-input__wrapper) {
      border-radius: 6px;
      padding: 0 20px;
      margin: 2px 4px;
      box-shadow: none !important;
      width: 100% !important;
    }

    .ic_search {
      width: 20px;
      height: 20px;
    }
  }
  .search-content {
    background-color: #fff;
    position: absolute;
    top: 128px;
    left: -6px;
    width: calc(100% - 40px);
    margin-left: 25px;
    max-height: 500px;
    z-index: 1000;
    border-radius: 8px;
    border: 1px solid rgba(158, 158, 158, 0.6);
    overflow-y: overlay;
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
      -webkit-border-radius: 4px;
      -moz-border-radius: 4px;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.2);
      -webkit-border-radius: 6px;
      -moz-border-radius: 6px;
      border-style: dashed;
      border-radius: 6px;
      border-color: transparent;
      border-width: 3px;
      background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-clip: border-box;
      background: rgba(0, 0, 0, 0.2);
      border-style: solid;
    }
    .scrollable {
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.5) transparent;
    }

    .scrollable:hover {
      scrollbar-color: rgba(0, 0, 0, 0.8) transparent;
    }
    &.contentPull {
      border-radius: 8px;
    }

    .content {
      height: 100%;
      background-color: #fff;
      overflow-y: overlay;
      // border-radius: 20px;
      border-bottom-left-radius: 20px;
      border-bottom-right-radius: 20px;
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
        -webkit-border-radius: 4px;
        -moz-border-radius: 4px;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        -webkit-border-radius: 6px;
        -moz-border-radius: 6px;
        border-style: dashed;
        border-radius: 6px;
        border-color: transparent;
        border-width: 3px;
        background-clip: padding-box;
      }

      &::-webkit-scrollbar-thumb:hover {
        background-clip: border-box;
        background: rgba(0, 0, 0, 0.2);
        border-style: solid;
      }
      .scrollable {
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.5) transparent;
      }

      .scrollable:hover {
        scrollbar-color: rgba(0, 0, 0, 0.8) transparent;
      }
      .item {
        padding: 10px 34px;
        cursor: pointer;
        &:hover {
          background: #f3f6fa;
        }
        .title {
          font-size: 16px;
          color: #000;
          font-weight: 700;
        }
        .desc {
          font-size: 14px;
          color: #000;
        }

        :deep(.highlightColor) {
          color: #2e70ff !important;
        }
      }
    }
  }
}
.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 300px;
  img {
    width: 160px;
    height: 160px;
    margin-top: 40px;
  }
  .tips {
    color: #1e253d;
    opacity: 0.6;
    font-size: 14px;
    margin-top: 10px;
  }
}
</style>
